import { HttpBackend, HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable, ViewContainerRef } from "@angular/core";
import { Observable, Subject, firstValueFrom } from "rxjs";
import { environment } from "../../../environment";
import { ActivatedRoute, Router } from "@angular/router";
import { SsoToken } from "./models/response-models/sso-token.response-model";
import { appRoute } from "src/app/app-route.constants";
import { captureReauthState } from "src/app/common/utils/myzpax-routes.util";
import { authTokenParam, ssoParam } from "./constants/sso-param.constants";
import { ssoParamValue } from "./constants/sso-param-value.constants";
import { API } from "./constants/api.constant";
import { User } from "../common/interfaces/user.interface";
import { StorageService } from "src/app/common/services/storage.service";
import { MatDialog } from "@angular/material/dialog";
import { SessionExpiredComponent } from "../features/login/session-expired/session-expired.component";
import {MatSnackBar} from '@angular/material/snack-bar';
import { Overlay } from '@angular/cdk/overlay';
import { LayoutService } from "src/app/services/layout.service";
import { MyZpaxService } from "src/app/services/myzpax.service";
import { LockAppMessageData, sendZpaxMessage } from "src/app/common/utils/myzpax-messaging";
@Injectable({
  providedIn: "root",
})
export class AuthService {
  private _httpClient = inject(HttpClient);
  private _httpBackend = inject(HttpBackend);
  private _httpClientWithoutInterceptor = new HttpClient(this._httpBackend);
  private _logout$ = new Subject<void>();
  private _router = inject(Router);
  private _dialog = inject(MatDialog);
  private _storageService = inject(StorageService);
  private _snackBar = inject(MatSnackBar);
  private overlay = inject(Overlay);
  private _layoutService = inject(LayoutService);
  private _myzpaxService = inject(MyZpaxService);
  constructor() {}
  get logout$() {
    return this._logout$.asObservable();
  }

  getTokenFromAuthorizationCode(code: string): Observable<SsoToken> {
    const url = environment.endpoint + API.Token;
    const body = {
      [authTokenParam.grantType]: ssoParamValue.grantTypeAuthorizationCode,
      [authTokenParam.grantValue]: code,
      [authTokenParam.redirectUri]: location.origin + "/" + appRoute.validate,
    };
    return this._httpClient.post<SsoToken>(url, body);
  }

  getRefreshToken(): Observable<SsoToken> {
    let url = environment.endpoint + API.Token;
    const body = {
      [authTokenParam.grantType]: ssoParamValue.grantTypeRefreshToken,
      [authTokenParam.grantValue]: this._storageService.getRefreshToken() ?? "",
    };
    return this._httpClientWithoutInterceptor.post<SsoToken>(url, body);
  }

  /**
   * Re-issues the access token so it reflects the user's current assignments.
   * The token lists the facilities the user may access, so a corporation granted
   * after login only becomes usable once the token is refreshed (RC-11021).
   */
  async refreshSession(): Promise<void> {
    const response = await firstValueFrom(this.getRefreshToken());
    this._storageService.updateTokens(response);
  }

  getUserInfo(): Observable<User> {
    const url = environment.endpoint + environment.userDetailsAPI;
    return this._httpClient.get<User>(url);
  }

  IsUserActive(): Observable<User> {
    const url = environment.endpoint + "/user/is-user-active";
    return this._httpClient.get<User>(url);
  }

  lockUserSession(lockType: LockAppMessageData['lockType'] = 'timeout') {
    if (this._myzpaxService.isInMyzpax) {
      this._storageService.lockUserSession();
      this._storageService.setSessionState();
      this._storageService.clearUserData();
      sendZpaxMessage('lock_app', { lockType, removeIframe: true, afterReAuthAction: 'none' });
      return;
    }
    this._storageService.lockUserSession();
    this.reAuthenticate();
  }
  handleForbiddenCall(){
    const snackBarRef = this._snackBar.open("User is not authorized to perform the action","", {duration: 5000,panelClass: ['forbidden-snackbar']});
    snackBarRef.afterOpened().subscribe(() => {
            const overlayRef = (snackBarRef as any)._overlayRef;
            if (overlayRef) {
              overlayRef.updatePositionStrategy(
                this.overlay.position().global().top('60px').right('0px')
              );
            }
      });
    }

  reAuthenticate(){
    this._storageService.setSessionState();
    this._storageService.clearUserData();

    if (this._myzpaxService.isInMyzpax) {
      const state = captureReauthState();
      this._router.navigate([`/${appRoute.init}`], { queryParams: { state } });
      return;
    }

    if (this._layoutService.inMobile) {
      return;
    }

    this._dialog.open(SessionExpiredComponent).afterClosed().subscribe(() => {
      this.redirectToSSOLoginPage();
    })
  }


  logout() {
    this._storageService.clearUserSession();

    if (this._myzpaxService.isInMyzpax) {
      return;
    }

    if (this._layoutService.inMobile) {
      return;
    }

    this.redirectToSSOLogoutPage();
  }

  redirectToSSOLogoutPage() {
    const redirectUrl = location.origin + "/" + appRoute.validate;
    const url =
      environment.ssoLogoutAPI +
      "?" +
      new HttpParams().set("redirectTo", redirectUrl).toString();
    window.location.href = url;
  }

  redirectToSSOLoginPage(state?: string) {
    const redirectUrl = location.origin + "/" + appRoute.validate;
    const params: Record<string, string> = {
      [ssoParam.clientId]: environment.clientId,
      [ssoParam.redirectUri]: redirectUrl,
      [ssoParam.responseType]: ssoParamValue.responseTypeCode,
      [ssoParam.scope]: ssoParamValue.scopeOfflineAccess + " " + ssoParamValue.scopeProfile,
      [ssoParam.fallbackUri]: window.location.origin + "/" + appRoute.marketing,
    };
    if (state) {
      params[ssoParam.state] = state;
    }
    // when in myzpax, prompt=none is need to trigger myzPAX's re-authentication flow in case of sso timeout 
    // instead of showing sso's re-authentication page. 
    if(this._myzpaxService.isInMyzpax) {
      params[ssoParam.prompt] = ssoParamValue.promptNone;
    }
    const url = environment.ssoConnectAPI + "?" + new HttpParams().appendAll(params).toString();
    window.location.replace(url);
  }

  handleSessionTimeout() {
    this.redirectToSSOLoginPage();
  }
}
